// =====================================================================
// src/lib/auditoria.js — o que a trilha de um apartamento mostra, sem DOM
//
// Puro. A trilha vem pronta do banco (0026): já veio filtrada pela
// permissão, pela imobiliária e sem valor de reserva — a INVARIANTE 2
// é cumprida lá, não aqui. O que sobra para este arquivo é o trabalho de
// leitura: dar nome a cada ação, agrupar por dia e dizer a hora no fuso
// do HOTEL, que não é o do aparelho de quem consulta.
// =====================================================================

// A cor/grupo é a ORIGEM do evento, não a gravidade: nada aqui é alarme,
// é registro. O rótulo é o que a recepção chamaria a coisa em voz alta.
export const ACOES = Object.freeze({
  'reserva.criada':          { grupo: 'reserva',  rotulo: 'Reserva criada' },
  'reserva.alterada':        { grupo: 'reserva',  rotulo: 'Reserva alterada' },
  'reserva.cancelada':       { grupo: 'reserva',  rotulo: 'Reserva cancelada' },
  'reserva.noshow':          { grupo: 'reserva',  rotulo: 'No-show' },
  'reserva.checkout':        { grupo: 'checkin',  rotulo: 'Check-out' },
  'hospede.criado':          { grupo: 'hospede',  rotulo: 'Hóspede cadastrado' },
  'hospede.alterado':        { grupo: 'hospede',  rotulo: 'Hóspede alterado' },
  'hospede.removido':        { grupo: 'hospede',  rotulo: 'Hóspede removido' },
  'hospede.checkin':         { grupo: 'checkin',  rotulo: 'Check-in' },
  'hospede.checkin_desfeito':{ grupo: 'checkin',  rotulo: 'Check-in desfeito' },
  'precheckin.convite':      { grupo: 'precheckin', rotulo: 'Convite de pré-check-in enviado' },
  'precheckin.conferido':    { grupo: 'precheckin', rotulo: 'Pré-check-in conferido' },
  'precheckin.recusado':     { grupo: 'precheckin', rotulo: 'Pré-check-in recusado' },
  'limpeza.iniciada':        { grupo: 'limpeza',  rotulo: 'Limpeza iniciada' },
  'limpeza.terminada':       { grupo: 'limpeza',  rotulo: 'Limpeza terminada' },
  'limpeza.aprovada':        { grupo: 'limpeza',  rotulo: 'Limpeza aprovada' },
  'limpeza.devolvida':       { grupo: 'limpeza',  rotulo: 'Limpeza devolvida' },
  'unidade.criada':          { grupo: 'unidade',  rotulo: 'Apartamento cadastrado' },
  'unidade.alterada':        { grupo: 'unidade',  rotulo: 'Apartamento alterado' },
  'unidade.desativada':      { grupo: 'unidade',  rotulo: 'Apartamento desativado' },
  'unidade.reativada':       { grupo: 'unidade',  rotulo: 'Apartamento reativado' },
  // os chamados (0029 · 0033 · 0059), que a trilha já recebia sem rótulo
  'chamado.aberto':          { grupo: 'chamado',  rotulo: 'Chamado aberto na ficha' },
  'chamado.intercorrencia':  { grupo: 'chamado',  rotulo: 'Intercorrência reportada' },
  'chamado.validado':        { grupo: 'chamado',  rotulo: 'Chamado validado pela supervisão' },
  'chamado.descartado':      { grupo: 'chamado',  rotulo: 'Chamado descartado pela supervisão' },
  'chamado.sem_solucao':     { grupo: 'chamado',  rotulo: 'Manutenção não resolveu' },
  'chamado.escalado':        { grupo: 'chamado',  rotulo: 'Proprietário avisado' },
  'chamado.devolvido':       { grupo: 'chamado',  rotulo: 'Chamado devolvido à manutenção' },
  'chamado.resolvido':       { grupo: 'chamado',  rotulo: 'Chamado resolvido' },
});

// Ação que o banco passe a gravar e que ninguém traduziu aqui aparece com
// o nome cru, e não some. Sumir seria pior: a trilha ficaria mentindo por
// omissão, e ninguém descobriria que falta um rótulo.
export function rotuloDaAcao(acao) {
  const c = ACOES[acao];
  if (c) return c.rotulo;
  return String(acao || '').replace(/[._]/g, ' ').replace(/^./, s => s.toUpperCase());
}
export function grupoDaAcao(acao) {
  const c = ACOES[acao];
  if (c) return c.grupo;
  return String(acao || '').split('.')[0] || 'outro';
}

const TIPOS_LIMPEZA = { saida: 'saída', entrada: 'entrada', troca: 'troca', avulsa: 'avulsa', arrumacao: 'arrumação' };

// A segunda linha do evento: o que o `detalhe` do banco tem de concreto.
// Nunca inventa; se não há nada de útil, devolve string vazia e a tela
// não desenha a linha.
export function detalheDoEvento(e) {
  const d = (e && e.detalhe) || {};
  const partes = [];
  if (e && e.reserva_numero) partes.push('Reserva ' + e.reserva_numero);
  if (d.tipo && TIPOS_LIMPEZA[d.tipo]) partes.push('Limpeza de ' + TIPOS_LIMPEZA[d.tipo]);
  if (typeof d.minutos === 'number') partes.push(d.minutos + ' min');
  if (d.nome) partes.push(d.nome);
  if (d.motivo) partes.push(d.motivo);
  if (d.campos && Array.isArray(d.campos) && d.campos.length) partes.push(d.campos.join(', '));
  if (d.pessoas) partes.push(d.pessoas + (d.pessoas === 1 ? ' pessoa' : ' pessoas'));
  if (d.de && d.para) partes.push(`${d.de} → ${d.para}`);
  return partes.join(' · ');
}

// O nome de quem fez. O pré-check-in não tem usuário do hotel por trás:
// dizer um nome ali seria inventar, e deixar em branco seria esconder.
export function quemDoEvento(e) {
  const q = e && e.quem_nome ? String(e.quem_nome).trim() : '';
  if (q) return { nome: q, sistema: false };
  if (e && String(e.acao || '').startsWith('precheckin.')) return { nome: 'pelo hóspede', sistema: true };
  return { nome: '—', sistema: true };
}

// A hora no fuso do HOTEL. Quem consulta pode estar em outro fuso, e a
// trilha é do prédio, não de quem lê.
export function horaDe(em, fuso) {
  if (!em) return '';
  const d = new Date(em);
  if (Number.isNaN(d.getTime())) return '';
  try {
    return new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: fuso || 'UTC' }).format(d);
  } catch { return new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' }).format(d); }
}
export function diaDe(em, fuso) {
  if (!em) return '';
  const d = new Date(em);
  if (Number.isNaN(d.getTime())) return '';
  const f = (tz) => new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: tz }).format(d);
  try { return f(fuso || 'UTC'); } catch { return f('UTC'); }
}

// A trilha agrupada por dia do hotel, do mais recente para o mais antigo,
// que é a ordem em que o banco já devolve.
export function porDia(linhas, { fuso, hoje } = {}) {
  const dias = [];
  const mapa = new Map();
  for (const e of linhas || []) {
    const dia = diaDe(e.em, fuso);
    if (!mapa.has(dia)) { mapa.set(dia, []); dias.push(dia); }
    mapa.get(dia).push(e);
  }
  return dias.map(dia => ({ dia, rotulo: rotuloDoDia(dia, hoje), eventos: mapa.get(dia) }));
}

const SEMANA = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];
const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
const maiuscula = s => s.charAt(0).toUpperCase() + s.slice(1);

// "Hoje · terça, 15 de setembro" / "Sexta, 11 de setembro". O dia da
// semana entra porque quem audita costuma lembrar "foi num sábado", não
// "foi dia 12".
export function rotuloDoDia(dia, hoje) {
  if (!dia) return '';
  const [a, m, d] = dia.split('-').map(Number);
  const dt = new Date(Date.UTC(a, m - 1, d));
  const semana = SEMANA[dt.getUTCDay()];
  const anoAtual = String(hoje || '').slice(0, 4);
  const data = `${d} de ${MESES[m - 1]}${anoAtual && String(a) !== anoAtual ? ` de ${a}` : ''}`;
  const ontem = hoje ? new Date(Date.parse(hoje + 'T00:00:00Z') - 86400000).toISOString().slice(0, 10) : null;
  if (hoje && dia === hoje) return `Hoje · ${semana}, ${data}`;
  if (ontem && dia === ontem) return `Ontem · ${semana}, ${data}`;
  return `${maiuscula(semana)}, ${data}`;
}

// "14 registros" / "1 registro" — e o vazio dito por extenso, porque
// "0 registros" numa tela de auditoria assusta sem motivo.
export function resumoDaTrilha(linhas) {
  const n = (linhas || []).length;
  if (!n) return 'Nada aconteceu neste apartamento no período.';
  return `${n} registro${n === 1 ? '' : 's'}`;
}

export function periodoPadrao(hoje, dias = 30) {
  const [a, m, d] = String(hoje).split('-').map(Number);
  const fim = new Date(Date.UTC(a, m - 1, d));
  const ini = new Date(fim.getTime() - (dias - 1) * 86400000);
  const iso = x => x.toISOString().slice(0, 10);
  return { de: iso(ini), ate: iso(fim) };
}
